import { isEmpty } from "lodash";
import { useCallback, useEffect, useMemo } from "react";

import { Filter, FilterId, FilterType } from "@/Components/v2/organism/Header/Header";
import { useInfo } from "@/context/InfoProvider/InfoProvider";
import { useAllFormats } from "@/pages/formats/api/getAllFormats";
import { CombinedNode } from "@/pages/scenario-builder/utils/nodes";
import { getMappedName } from "@/Utils/mapping-utils";

import { useFormatsStore } from "./store/useFormatsStore";

export const useAvailableFilters = ({
  selectedNode,
  startDate,
  endDate,
}: {
  selectedNode?: CombinedNode;
  startDate?: string;
  endDate?: string;
}) => {
  const { setTargetNode, setChannels, setFormats, setGeographies } = useFormatsStore();
  const { targetNode, channels, geographies, formats } = useFormatsStore((state) => state.filters);
  const { initInfo: info } = useInfo();
  const [geoData] = info?.geographies || [];

  const { data: allFormats } = useAllFormats({
    startDate,
    endDate,
    brand: selectedNode?.node.brand ?? "default",
    category: selectedNode?.node.category ?? "default",
    product: selectedNode?.node.product ?? "default",
  });

  const handleTargetNodeChange = useCallback(
    (targetNode: string | null) => {
      setTargetNode(targetNode);
    },
    [setTargetNode]
  );
  const handleChannelsChange = useCallback(
    (channel: string[]) => setChannels(channel),
    [setChannels]
  );

  const handleFormatsChange = useCallback((format: string[]) => setFormats(format), [setFormats]);

  const handleGeographiesChange = useCallback(
    (geography: string[]) => setGeographies(geography),
    [setGeographies]
  );

  const formatOptions = useMemo(() => {
    if (!allFormats) return [];

    return allFormats
      .map((format) => ({
        label: getMappedName(format) || format,
        value: format,
        selected: false,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [allFormats]);

  const targetNodeFilter = useMemo(
    () => ({
      id: FilterId.BusinessUnit,
      label: "Business Unit",
      type: FilterType.BusinessUnit,
      value: targetNode,
      onChange: handleTargetNodeChange,
    }),
    [targetNode, handleTargetNodeChange]
  );

  const channelsFilter = useMemo(
    () => ({
      id: FilterId.Channel,
      label: "Channels",
      type: FilterType.MultiSelect,
      value: channels,
      onChange: handleChannelsChange,
    }),
    [channels, handleChannelsChange]
  );

  const formatsFilter = useMemo(
    () => ({
      id: FilterId.Format,
      label: "Formats",
      type: FilterType.MultiSelect,
      value: formats ?? [],
      onChange: handleFormatsChange,
      options: formatOptions,
    }),
    [formats, handleFormatsChange, formatOptions]
  );

  const geographiesFilter = useMemo(
    () => ({
      id: FilterId.Geography,
      label: "Geo",
      type: FilterType.MultiSelect,
      value: geographies,
      onChange: handleGeographiesChange,
    }),
    [geographies, handleGeographiesChange]
  );

  useEffect(() => {
    if (!isEmpty(allFormats)) {
      setFormats(allFormats || []);
    }
  }, [allFormats, setFormats]);

  return useMemo(() => {
    const filters: Filter[] = [targetNodeFilter, channelsFilter, formatsFilter];
    if (geoData && geoData.length > 0) {
      filters.push(geographiesFilter);
    }

    return filters;
  }, [targetNodeFilter, channelsFilter, geographiesFilter, geoData, formatsFilter]);
};
